import { BetTypes } from '../../types/bet';
import type { BetTemplate, BetType } from '../../types/bet';
import { type MarketId, MarketIds } from '../../types/market';

import type { MatchState } from './matches';
import { PlayerBasketStats } from './players';

export type BetId = number;

export type BetState = {
  betId: BetId;
  available: boolean;
  betGroup: number;
  template: BetTemplate;
  betTypeIsLive?: boolean;
  betTypeName?: string;
  betTypeHelp: string;
  betTypeCategory: string;
  betTypeCategoryId: number;
  betTitle: string;
  betType?: BetType;
  betTypeId?: BetType;
  outcomes: Array<number>;
  specialBetValue?: string;
  isAlternativeMainBet: boolean;
  marketId: number;
  helperText: string | null;
  previousOdd?: number;
  noCombo?: number;
  isHighlighted?: boolean;
  playerStats?: keyof PlayerBasketStats;
  matchId?: number; // so far (24/11/2023) only available from betslip API response,
  currentTotal?: number;
  marketTotal?: number;
};

export type EntitiesBets = {
  [betId: BetId]: BetState;
};

const checkMarketType = (type: MarketId, bet: BetState): boolean => {
  if (!bet || !bet.marketId) {
    return false;
  }

  return type === bet.marketId;
};

const checkBetType = (type: BetType, bet: BetState): boolean => {
  if (!bet || !bet.betType) {
    return false;
  }

  return type === bet.betType;
};

const checkSbv = (sbv: string, bet: BetState) => {
  if (!bet || !bet.specialBetValue) {
    return false;
  }

  return bet.specialBetValue.indexOf(sbv) !== -1;
};

class BetEntity {
  static isMyMatchMainBet = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return !![
        BetTypes.SOCCER_RESULT,
        BetTypes.BASKETBALL_WINNER,
        BetTypes.TENNIS_WINNER,
        BetTypes.FOOTBALL_WINNER,
        BetTypes.ICE_HOCKEY_WINNER,
        BetTypes.RUGBY_WINNER,
        BetTypes.RUGBY_13_WINNER,
      ].find((betType) => betType === bet.betType);
    }

    return false;
  };

  static isSoccerMultiscorersPrematch = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return (
        bet.betType === BetTypes.SOCCER_MULTI_SCORERS &&
        !!['goals=2', 'goals=3', 'goals=4'].find((sbv) => sbv === bet.specialBetValue)
      );
    }

    return false;
  };

  static isSoccerMultiScorers2or3Live = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return !![BetTypes.SOCCER_MULTI_SCORERS_2, BetTypes.SOCCER_MULTI_SCORERS_3].find(
        (betType) => betType === bet.betType,
      );
    }

    return false;
  };

  static isSoccerScorersPrematch = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return !![BetTypes.SOCCER_ANYTIME_SCORER, BetTypes.SOCCER_FIRST_SCORER, BetTypes.SOCCER_LAST_SCORER].find(
        (betType) => betType === bet.betType,
      );
    }
    return false;
  };

  static isSoccerAsianHandicap = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return !![
        BetTypes.SOCCER_ASIAN_HANDICAP_CORNER,
        BetTypes.SOCCER_ASIAN_HANDICAP_CORNER_FIRST_HALF,
        BetTypes.SOCCER_ASIAN_HANDICAP_GOAL_DIFFERENCE,
        BetTypes.SOCCER_ASIAN_HANDICAP_GOAL_DIFFERENCE_WX,
        BetTypes.SOCCER_ASIAN_HANDICAP_GOAL_DIFFERENCE_OVERTIME,
        BetTypes.SOCCER_ASIAN_HANDICAP_GOAL_DIFFERENCE_FIRST_HALF,
        BetTypes.SOCCER_ASIAN_HANDICAP_GOAL_DIFFERENCE_FIRST_HALF_OVERTIME,
      ].find((betType) => betType === bet.betType);
    }
    return false;
  };

  static isFootballScorers = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return (
        (BetTypes.FOOTBALL_SIMPLE_SCORER === bet.betType || BetTypes.FOOTBALL_MULTI_SCORER === bet.betType) &&
        // eslint-disable-next-line sonarjs/no-duplicate-string
        !!['type=prematch', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue)
      );
    }
    return false;
  };

  static isRugbyScorers = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_MULTI_SCORER === bet.betType) &&
        !!['type=prematch', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur prelive
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=prematch', bet) &&
          (checkBetType(BetTypes.RUGBY_SIMPLE_SCORER, bet) || checkBetType(BetTypes.RUGBY_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isRugbyLeagueScorers = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_LEAGUE_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_LEAGUE_MULTI_SCORER === bet.betType) &&
        !!['type=prematch', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur prelive
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=prematch', bet) &&
          (checkBetType(BetTypes.RUGBY_LEAGUE_SIMPLE_SCORER, bet) ||
            checkBetType(BetTypes.RUGBY_LEAGUE_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isRugby7sScorers = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_7S_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_7S_MULTI_SCORER === bet.betType) &&
        !!['type=prematch', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur prelive
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=prematch', bet) &&
          (checkBetType(BetTypes.RUGBY_7S_SIMPLE_SCORER, bet) || checkBetType(BetTypes.RUGBY_7S_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isRugbyScorersLive = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_MULTI_SCORER === bet.betType) &&
        !!['type=live', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur live
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=live', bet) &&
          (checkBetType(BetTypes.RUGBY_SIMPLE_SCORER, bet) || checkBetType(BetTypes.RUGBY_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isRugbyLeagueScorersLive = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_LEAGUE_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_LEAGUE_MULTI_SCORER === bet.betType) &&
        !!['type=live', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur live
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=live', bet) &&
          (checkBetType(BetTypes.RUGBY_LEAGUE_SIMPLE_SCORER, bet) ||
            checkBetType(BetTypes.RUGBY_LEAGUE_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isRugby7sScorersLive = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet && bet.betType) {
      const eligible =
        (BetTypes.RUGBY_7S_SIMPLE_SCORER === bet.betType || BetTypes.RUGBY_7S_MULTI_SCORER === bet.betType) &&
        !!['type=live', 'goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue);

      if (eligible) {
        // On vérifie qu'on dispose du bet marqueur live
        const checkEligibility = (bet: BetState) =>
          checkSbv('type=live', bet) &&
          (checkBetType(BetTypes.RUGBY_7S_SIMPLE_SCORER, bet) || checkBetType(BetTypes.RUGBY_7S_MULTI_SCORER, bet));

        return matchBets.filter(checkEligibility).length > 0;
      }
    }
    return false;
  };

  static isIceHockeyScorers = (bet: BetState): boolean => {
    if (bet && bet.marketId && bet.betType) {
      if (
        (BetTypes.ICE_HOCKEY_SIMPLE_SCORER === bet.betType || BetTypes.ICE_HOCKEY_MULTI_SCORER === bet.betType) &&
        // Issue #829
        (MarketIds.MARKET_ID_ICE_HOCKEY_ANYTIME_GOALSCORER === bet.marketId ||
          MarketIds.MARKET_ID_ICE_HOCKEY_MULTI_GOALSCORER === bet.marketId) &&
        (!bet.specialBetValue || bet.specialBetValue.indexOf('version=') === -1)
      ) {
        return true;
      }
    }
    return false;
  };

  static isIceHockeyScorerPeriod = (bet: BetState): boolean => {
    return (
      checkMarketType(MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_FIRST_PERIOD, bet) ||
      checkMarketType(MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_SECOND_PERIOD, bet) ||
      checkMarketType(MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_THIRD_PERIOD, bet)
    );
  };

  static isIceHockeyScorerLive = (bet: BetState, matchBets: Array<BetState>): boolean => {
    const checkEligibility = (bet: BetState) =>
      checkMarketType(MarketIds.MARKET_ID_ICE_HOCKEY_ANYTIME_GOALSCORER, bet) ||
      checkMarketType(MarketIds.MARKET_ID_NEXT_GOALSCORER, bet);

    const eligible = checkEligibility(bet);

    if (eligible) {
      return matchBets.filter(checkEligibility).length === 2;
    }
    return false;
  };

  static isIceHockeyMultiScorer = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return (
        checkMarketType(MarketIds.MARKET_ID_MULTIPLE_GOALSCORER, bet) &&
        !!['goals=2', 'goals=3'].find((sbv) => sbv === bet.specialBetValue)
      );
    }
    return false;
  };

  static isSoccerAsianTextHandicap = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return BetTypes.SOCCER_ASIANTEXT_HANDICAP_GOAL_DIFFERENCE === bet.betType;
    }
    return false;
  };

  static isSoccerScorerGoalType = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return BetTypes.SOCCER_SCORER_GOAL_TYPE === bet.betType;
    }
    return false;
  };

  static isSoccerScorerVsCompetitor = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return (
        BetTypes.SOCCER_SCORER_VS_COMPETITOR_1 === bet.betType || BetTypes.SOCCER_SCORER_VS_COMPETITOR_2 === bet.betType
      );
    }
    return false;
  };

  static isSoccerScorerVsScorer = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return BetTypes.SOCCER_SCORER_VS_SCORER === bet.betType;
    }
    return false;
  };

  static isSoccerScorerAndTeamWins = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return BetTypes.SOCCER_SCORER_AND_TEAM_WINS === bet.betType;
    }
    return false;
  };

  static isSoccerNextScorerOvertime = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return checkMarketType(MarketIds.MARKET_ID_OVERTIME_NEXT_SCORER, bet);
    }
    return false;
  };

  static isSoccerScorerOvertime = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME, bet);
    }
    return false;
  };

  static isSoccerOver2ScorerOvertime = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return checkMarketType(MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_OVERTIME, bet);
    }
    return false;
  };

  static isSoccerScorerOvertimeFirstHaltTime = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME_1_HT, bet);
    }
    return false;
  };

  static isSoccerScorerOvertimeSecondHaltTime = (bet: BetState): boolean => {
    if (bet && bet.marketId) {
      return checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME_2_HT, bet);
    }
    return false;
  };

  static isSoccerNumberCard = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return BetTypes.SOCCER_NUMBER_OF_CARDS_BY_PLAYER === bet.betType;
    }
    return false;
  };

  static isSoccerScorersLive = (bet: BetState, match: MatchState | null, matchBets: Array<BetState>): boolean => {
    const checkEligibility = (bet: BetState) =>
      checkBetType(BetTypes.SOCCER_ANYTIME_SCORER, bet) ||
      checkBetType(BetTypes.SOCCER_NEXT_SCORER_LIVE, bet) ||
      checkBetType(BetTypes.SOCCER_LAST_SCORER, bet);

    const eligible = checkEligibility(bet);

    if (eligible) {
      // On verifie qu'il y ait les 3 markets de disponible sur le match
      return matchBets.filter(checkEligibility).length === 3;
    }
    return false;
  };

  static isSoccerScorersLiveWithoutLastScorer = (
    bet: BetState,
    match: MatchState | null,
    matchBets: Array<BetState>,
  ): boolean => {
    const checkEligibility = (bet: BetState) =>
      checkBetType(BetTypes.SOCCER_ANYTIME_SCORER, bet) || checkBetType(BetTypes.SOCCER_NEXT_SCORER_LIVE, bet);

    const eligible = checkEligibility(bet);

    if (eligible) {
      // On verifie qu'il y ait les 2 markets de disponible sur le match
      return matchBets.filter(checkEligibility).length === 2;
    }
    return false;
  };

  static isSoccerCompetitorGoalType = (
    bet: BetState,
    match: MatchState | null,
    matchBets: Array<BetState>,
  ): boolean => {
    const checkEligibility = (bet: BetState) =>
      checkMarketType(MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_PENALTY, bet) ||
      checkMarketType(MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_HEAD, bet) ||
      checkMarketType(MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_FREEKICK, bet);

    const eligible = checkEligibility(bet);

    // Si on est sur 1 des 3 markets
    if (eligible) {
      // On verifie qu'il y ait les 3 markets de disponible sur le match
      return matchBets.filter(checkEligibility).length === 3;
    }
    return false;
  };

  static isDuoScorer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_GOALSCORER_DUO, bet);
  };

  static isTrioScorer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_GOALSCORER_TRIO, bet);
  };

  static isSoccerScorerPeriod10Minutes = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_10MINUTES, bet);
  };

  static isSoccerScorerPeriod15Minutes = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_15MINUTES, bet);
  };

  static isSoccerScorerPeriodHalfTime = (bet: BetState): boolean => {
    return (
      checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_1_HT, bet) ||
      checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_2_HT, bet) ||
      checkMarketType(MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_BOTH_HT, bet)
    );
  };

  static isSoccerPlayerPropAssists = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS, bet);
  };

  static isSoccerPlayerPropShots = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_SHOTS, bet);
  };

  static isSoccerPlayerPropShotsOnGoal = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_SHOTS_ON_GOAL, bet);
  };

  static isSoccerPlayerPropPasses = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_PASSES, bet);
  };

  static isSoccerPlayerPropTackles = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_TACKLES, bet);
  };

  static isSoccerMultiScorerHalfTime1 = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_1_HT, bet);
  };

  static isSoccerMultiScorerHalfTime2 = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_2_HT, bet);
  };

  static isBasketballPointPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_POINTS, bet);
  };

  static isBasketballDoubleChancePlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_DOUBLE_CHANCE, bet);
  };

  static isBasketballSumPoint2Players = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_2PLAYERS_SUM_POINT, bet);
  };

  static isBasketballSumPoint3Players = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_3PLAYERS_SUM_POINT, bet);
  };

  static isBasketballPassPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_PASS, bet);
  };

  static isBasketballReboundPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_REBOUND, bet);
  };

  static isBasketball3PointPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_3POINTS, bet);
  };

  static isBasketballPointPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_POINT_SPECIAL, bet);
  };

  static isBasketballPassPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_PASS_OVER_UNDER, bet);
  };

  static isBasketballReboundPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_REBOUND_OVER_UNDER, bet);
  };

  static isBasketball3PointPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_3POINTS_OVER_UNDER, bet);
  };

  static isBasketballPointReboundPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_POINT_REBOUND_ASSIST_OVER_UNDER, bet);
  };

  static isBasketballPointPassPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_POINT_PASS_OVER_UNDER, bet);
  };

  static isBasketballPassReboundPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_PASS_REBOUND_OVER_UNDER, bet);
  };

  static isBasketballPointRebondPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_POINT_REBOUND_OVER_UNDER, bet);
  };

  static isBasketballInterceptPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_INTERCEPT_OVER_UNDER, bet);
  };

  static isBasketballBlockPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_BLOCK_OVER_UNDER, bet);
  };

  static isBasketballLostBallPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_LOST_BALL_OVER_UNDER, bet);
  };

  static isBasketballTotalInterceptBlockPlayerOverUnder = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_INTERCEPT_BLOCK_OVER_UNDER, bet);
  };

  static isBasketballTotalPointsReboundAssistPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND_ASSIST, bet);
  };

  static isBasketballTotalPointsAssistPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_ASSIST, bet);
  };

  static isBasketballTotalAssistReboundPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_ASSIST_REBOUND, bet);
  };

  static isBasketballTotalPointsReboundPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND, bet);
  };

  static isBasketballInterceptPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_INTERCEPT, bet);
  };

  static isBasketballBlockPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_BLOCK, bet);
  };

  static isBasketballLostBallPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_PLAYER_LOST_BALL, bet);
  };

  static isBasketballTotalInterceptBlockPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_TOTAL_PLAYER_INTERCEPT_BLOCK, bet);
  };

  static isBasketballNumberOfPointsHeadToHeadPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_BASKETBALL_NUMBER_OF_POINTS_PLAYER_HEAD_TO_HEAD, bet);
  };

  static isBasketballNumberOfAssistsHeadToHeadPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_BASKETBALL_NUMBER_OF_ASSISTS_PLAYER_HEAD_TO_HEAD, bet);
  };

  static isBasketballNumberOfReboundsHeadToHeadPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_BASKETBALL_NUMBER_OF_REBOUNDS_PLAYER_HEAD_TO_HEAD, bet);
  };

  static isHandballScorersPlayer = (bet: BetState): boolean => {
    return checkMarketType(MarketIds.MARKET_ID_ICE_HOCKEY_MULTI_GOALSCORER, bet);
  };

  static allowPlayerStatsTracker = (bet: BetState): boolean => {
    if (bet && bet.betType) {
      return !![
        BetTypes.SOCCER_SCORER_AND_TEAM_WINS,
        BetTypes.BASKETBALL_PLAYER_TEAM2_POINTS,
        BetTypes.BASKETBALL_PLAYER_TEAM2_REBOUNDS,
        BetTypes.BASKETBALL_PLAYER_TEAM2_ASSISTS,
        BetTypes.BASKETBALL_PLAYER_TEAM1_POINTS,
        BetTypes.BASKETBALL_PLAYER_TEAM1_REBOUNDS,
        BetTypes.BASKETBALL_PLAYER_TEAM1_ASSISTS,
      ].find((betType) => betType === bet.betType);
    }
    return false;
  };

  static getBasketTeam = (bet: BetState): number | null => {
    if (bet && bet.betType) {
      if (
        [
          BetTypes.BASKETBALL_PLAYER_TEAM1_POINTS,
          BetTypes.BASKETBALL_PLAYER_TEAM1_REBOUNDS,
          BetTypes.BASKETBALL_PLAYER_TEAM1_ASSISTS,
          BetTypes.BASKETBALL_PLAYER_TEAM1_TRIPLE,
        ].find((betType) => betType === bet.betType)
      ) {
        return 1;
      }
      if (
        [
          BetTypes.BASKETBALL_PLAYER_TEAM2_POINTS,
          BetTypes.BASKETBALL_PLAYER_TEAM2_REBOUNDS,
          BetTypes.BASKETBALL_PLAYER_TEAM2_ASSISTS,
          BetTypes.BASKETBALL_PLAYER_TEAM2_TRIPLE,
        ].find((betType) => betType === bet.betType)
      ) {
        return 2;
      }
    }
    return null;
  };

  static isSoccerDecisivePlayer = (bet: BetState, matchBets: Array<BetState>): boolean => {
    if (bet?.marketId === MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER) {
      // On verifie qu'il y ait les 3 markets de disponible sur le match
      const foundMarkets = new Set();

      matchBets.forEach((bet: BetState) => {
        if (
          checkMarketType(MarketIds.MARKET_ID_ANYTIME_GOALSCORER, bet) ||
          checkMarketType(MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS, bet) ||
          checkMarketType(MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER, bet)
        ) {
          foundMarkets.add(bet.marketId);
        }
      });

      return foundMarkets.size === 3;
    }

    return false;
  };
}

export { BetEntity };
